import type { CardBrandType } from '@subscriptions/domain/types';
import { CardBrands } from '@subscriptions/domain/types';

/**
 * Validated card brand value object (null for non-card payment methods)
 */
export type CardBrand = Readonly<CardBrandType | null>;

const validBrands = Object.values(CardBrands) as string[];

/**
 * Creates a validated CardBrand value object.
 *
 * The backend sends the brand lowercase (e.g. `visa`, `mastercard`, `amex`), so the raw
 * value is normalized to the canonical uppercase form ({@link CardBrands}) before validating.
 *
 * @param raw - Raw brand string, null, or undefined (missing from API response)
 * @returns Validated CardBrand in canonical uppercase form (null when raw is null or undefined)
 * @throws Error if brand is a string that does not match a known value
 */
export const createCardBrand = (raw: string | null | undefined): CardBrand => {
  if (raw === null || raw === undefined) {
    return null;
  }
  const normalized = raw.trim().toUpperCase();
  if (!validBrands.includes(normalized)) {
    throw new Error(`Invalid card brand: ${raw}. Must be one of: ${validBrands.join(', ')} or null`);
  }
  return normalized as CardBrand;
};
